import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
export default function SolutionsPage() {
  const { t } = useTranslation(['solutions', 'common']);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Definir meta tags SEO
    document.title = "Soluções em Engenharia Patrimonial | FGT Capital São Paulo";
    
    // Adicionar Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ServicePage",
      "name": "Soluções - FGT Capital",
      "description": "Conheça nossas soluções em engenharia patrimonial: aquisição imobiliária, construção, planejamento patrimonial e estruturação empresarial via consórcios regulados.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/solucoes`,
      "provider": {
        "@type": "Organization",
        "name": "FGT Capital",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Serviços de Engenharia Patrimonial",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Aquisição Imobiliária",
                "description": "Estruturação financeira para aquisição de imóveis premium através de consórcios regulados",
                "serviceType": "Engenharia Patrimonial",
                "areaServed": "São Paulo, Brasil"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Construção e Reforma",
                "description": "Planejamento financeiro para projetos de construção e reforma via consórcios",
                "serviceType": "Engenharia Patrimonial",
                "areaServed": "São Paulo, Brasil"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Planejamento Patrimonial",
                "description": "Arquitetura financeira personalizada para crescimento patrimonial estruturado",
                "serviceType": "Planejamento Financeiro",
                "areaServed": "São Paulo, Brasil"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Estruturação Empresarial",
                "description": "Soluções corporativas para expansão e aquisição de ativos empresariais",
                "serviceType": "Consultoria Empresarial",
                "areaServed": "São Paulo, Brasil"
              }
            }
          ]
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const solutions = t('solutions:items', { returnObjects: true }) as Array<{
    id: string;
    title: string;
    forWho: { label: string; text: string };
    whenMakesSense: { label: string; text: string };
    variables: { label: string; text: string };
    howFgt: { label: string; text: string };
  }>;

  return (
    <div className="min-h-screen bg-[#F6F2EC]">

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/2339f5b8b05efee05e78851e9bf11fe2.jpg"
            alt="Soluções FGT"
            className="w-full h-full object-cover object-top"
          />
        </div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B]/90 via-[#0B0B0B]/50 to-[#0B0B0B]/15"></div>
        {/* Accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#F97700]"></div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-20 pt-48">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F97700] mb-6">
              {t('solutions:hero.label', 'Soluções')}
            </p>
            <h1 className="text-5xl md:text-7xl font-semibold text-white leading-[1.05] mb-8 drop-shadow-lg tracking-tight">
              {t('solutions:hero.title')}
            </h1>
            <p className="text-xl text-white/80 leading-relaxed max-w-2xl mx-auto">
              {t('solutions:hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="pb-32 px-6">
        <div className="max-w-6xl mx-auto space-y-12">
          {solutions.map((solution, index) => (
            <div
              key={solution.id}
              className="bg-[#FBF8F3] border border-[#E7E1D9] p-10 transition-all duration-300 hover:border-[#F97700]/30"
              style={{
                opacity: 0,
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s forwards`
              }}
            >
              <h2 className="text-2xl font-semibold text-[#0B0B0B] mb-8 tracking-tight">
                {solution.title}
              </h2>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xs font-semibold text-[#F97700] uppercase tracking-wider mb-3">
                    {solution.forWho.label}
                  </h3>
                  <p className="text-[#2E2E2E] leading-relaxed text-sm">
                    {solution.forWho.text}
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-[#F97700] uppercase tracking-wider mb-3">
                    {solution.whenMakesSense.label}
                  </h3>
                  <p className="text-[#2E2E2E] leading-relaxed text-sm">
                    {solution.whenMakesSense.text}
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-[#F97700] uppercase tracking-wider mb-3">
                    {solution.variables.label}
                  </h3>
                  <p className="text-[#2E2E2E] leading-relaxed text-sm">
                    {solution.variables.text}
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-[#F97700] uppercase tracking-wider mb-3">
                    {solution.howFgt.label}
                  </h3>
                  <p className="text-[#2E2E2E] leading-relaxed text-sm">
                    {solution.howFgt.text}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#0B0B0B]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-semibold text-white mb-4 tracking-tight">
            {t('solutions:cta.title')}
          </h2>
          <p className="text-white/70 mb-8 text-sm">
            {t('solutions:cta.subtitle')}
          </p>
          <a
            href="/diagnostico"
            className="inline-block bg-[#F97700] text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#E56E00] transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('solutions:cta.button')}
          </a>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}